import { log } from '@/logger/logger.ts';
import { withRetry } from '@/utils/async.utils.ts';
import { resolveFontFileUrl } from '@/utils/fontAssetUtils.ts';
import { LOCAL_MODEL_KEY_PREFIX } from '@/consts/localModel.consts';

const logger = log.getLogger('Wasm');

/** Fallback for stalled media requests that emit neither loadeddata nor error. */
const VIDEO_FIRST_FRAME_TIMEOUT_MS = 60_000;

// Holds dropped 3D bytes keyed by their `local:` key so the loader can serve them
// without a network round-trip. Freed once WASM has parsed the model.
const localModelBytes = new Map<string, Uint8Array>();

export function registerLocalModelBytes(key: string, bytes: Uint8Array): void {
  localModelBytes.set(key, bytes);
}

export function releaseLocalModelBytes(key: string): void {
  localModelBytes.delete(key);
}

export function clearLocalModelBytes(): void {
  localModelBytes.clear();
}

// Generic loading state and cache management
const loadingPromises = new Map<string, Promise<unknown>>();

interface LoaderConfig<T> {
  loader: (url: string) => Promise<T>;
  cacheKey?: string; // In-flight-dedup identity. Defaults to `url` if not passed explicitly.
}

const GLB_EXTENSION_PATTERN = /\.glb(?:[?#]|$)/i;
const GLB_HEADER_LENGTH = 12;
const GLB_MAGIC = 0x46546c67;

/**
 * Reject empty/truncated/non-GLB payloads before WASM deserialize.
 * Truncated HTTP 2xx bodies otherwise bypass `withRetry` and surface as
 * `could not completely read the object` from the glTF parser.
 */
export function validateThreeDModelBytes(url: string, bytes: Uint8Array): void {
  const expectsGlb = GLB_EXTENSION_PATTERN.test(url);

  if (bytes.byteLength === 0) {
    throw new Error(`Empty 3D model response for ${url}`);
  }

  if (bytes.byteLength < Uint32Array.BYTES_PER_ELEMENT) {
    if (expectsGlb) {
      throw new Error(`Invalid GLB response for ${url}: read ${bytes.byteLength} bytes`);
    }
    return;
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const isGlb = view.getUint32(0, true) === GLB_MAGIC;

  if (!isGlb) {
    if (expectsGlb) {
      throw new Error(`Invalid GLB response for ${url}: missing glTF binary header`);
    }
    return;
  }

  if (bytes.byteLength < GLB_HEADER_LENGTH) {
    throw new Error(`Invalid GLB header for ${url}: read ${bytes.byteLength} bytes`);
  }

  // Only a short body is a truncation. Trailing bytes past the declared length are ignorable
  // per the glTF spec, and the parser reads such files fine, so they must not be rejected.
  const declaredLength = view.getUint32(8, true);
  if (declaredLength > bytes.byteLength) {
    throw new Error(`Incomplete GLB response for ${url}: expected ${declaredLength} bytes, read ${bytes.byteLength}`);
  }
}

/**
 * Generic file loader with caching and promise deduplication
 */
async function loadFile<T>(url: string, config: LoaderConfig<T>): Promise<T> {
  const cacheKey = config.cacheKey ?? url;

  // Check if resource is currently being loaded
  if (loadingPromises.has(cacheKey)) {
    return loadingPromises.get(cacheKey) as Promise<T>;
  }

  const loadingPromise = withRetry(() => config.loader(url))
    .then((result) => {
      if (!result) {
        throw new Error('Loader resolved with no result');
      }
      return result;
    })
    .finally(() => {
      loadingPromises.delete(cacheKey);
    });

  loadingPromises.set(cacheKey, loadingPromise);
  return loadingPromise;
}

// Specific loader implementations
export const MAX_IMAGE_DIMENSION = 4096;

/**
 * The size to decode an image at, preserving aspect ratio. Returns undefined when it already
 * fits, so the caller can skip resizing entirely rather than resample at 1:1.
 */
export function clampedImageSize(
  width: number,
  height: number,
  maxDimension = MAX_IMAGE_DIMENSION,
): { width: number; height: number } | undefined {
  const longest = Math.max(width, height);
  if (longest <= maxDimension) {
    return undefined;
  }
  const scale = maxDimension / longest;
  // Never round down to zero: a sufficiently extreme aspect ratio would otherwise
  // produce a 0-height bitmap, which `createImageBitmap` rejects.
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * A bitmap carries its own alpha flag, which WebGL honours in place of `UNPACK_PREMULTIPLY_ALPHA`.
 * The renderer works in premultiplied alpha throughout, but premultiplying is only correct in
 * linear light, and left at the browser default the bitmap arrives premultiplied against its
 * sRGB-encoded values. Decoding straight lets `SourceTextureMaterial` linearise first and
 * premultiply afterwards; the browser's ordering shows up as dark fringing around transparent
 * edges.
 */
const IMAGE_BITMAP_ALPHA = { premultiplyAlpha: 'none' } as const;

/**
 * An image decoded under the ceiling, and whether fitting it there took any resampling.
 */
export interface DecodedImage {
  bitmap: ImageBitmap;
  resampled: boolean;
}

const SVG_URL_PATTERN = /\.svg(?:$|[?#])/i;

/**
 * Chrome's `createImageBitmap` rejects SVGs that `<img>` rasterizes, including Recraft Vectorizer
 * output. The node card still paints through `<img>`; this is the WebGL texture path.
 */
function isSvgImage(url: string, blob: Blob): boolean {
  return blob.type === 'image/svg+xml' || SVG_URL_PATTERN.test(url);
}

async function clampDecodedBitmap(decoded: ImageBitmap, maxDimension: number): Promise<DecodedImage> {
  const clamped = clampedImageSize(decoded.width, decoded.height, maxDimension);
  if (!clamped) {
    return { bitmap: decoded, resampled: false };
  }
  try {
    const bitmap = await createImageBitmap(decoded, {
      ...IMAGE_BITMAP_ALPHA,
      resizeWidth: clamped.width,
      resizeHeight: clamped.height,
      resizeQuality: 'high',
    });
    return { bitmap, resampled: true };
  } finally {
    // The full-resolution decode is dead either way; without this its memory
    // lingers until GC, which is exactly the pressure the clamp exists to avoid.
    decoded.close();
  }
}

/**
 * Rasterizes an SVG the bitmap decoder rejected. The blob URL stays same-origin, so the canvas
 * draw is not tainted, and the original SVG remains the stored asset.
 */
async function rasterizeSvgBlob(blob: Blob, maxDimension: number): Promise<DecodedImage> {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.src = objectUrl;
    await image.decode();
    const clamped = clampedImageSize(image.naturalWidth, image.naturalHeight, maxDimension);
    const width = clamped?.width ?? image.naturalWidth;
    const height = clamped?.height ?? image.naturalHeight;
    if (width < 1 || height < 1) {
      throw new Error('SVG has no intrinsic size');
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context is unavailable');
    }
    context.drawImage(image, 0, 0, width, height);
    return {
      bitmap: await createImageBitmap(canvas, IMAGE_BITMAP_ALPHA),
      resampled: clamped !== undefined,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Decodes the image at `url` to an `ImageBitmap`, resampled to fit `maxDimension`.
 */
export async function decodeClampedImage(url: string, maxDimension = MAX_IMAGE_DIMENSION): Promise<DecodedImage> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error('Failed to load image: ' + url + `: HTTP ${res.status}: ${res.statusText}`);
  }
  const blob = await res.blob();
  try {
    return await clampDecodedBitmap(await createImageBitmap(blob, IMAGE_BITMAP_ALPHA), maxDimension);
  } catch (error) {
    if (!isSvgImage(url, blob)) {
      throw error;
    }
    return rasterizeSvgBlob(blob, maxDimension);
  }
}

async function createImageBitmapLoader(url: string): Promise<ImageBitmap> {
  return (await decodeClampedImage(url)).bitmap;
}

function createVideoLoader(url: string): Promise<HTMLVideoElement> {
  const video = document.createElement('video');
  video.crossOrigin = 'Anonymous';
  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.loop = true;
  video.autoplay = false;
  video.src = url;
  return Promise.resolve(video);
}

/** Wait for drawable video data, releasing the loading UI on failure or a stalled request. */
export function waitForVideoFrame(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2 || video.error) return Promise.resolve();
  return new Promise((resolve) => {
    const finish = () => {
      clearTimeout(timeout);
      video.removeEventListener('loadeddata', finish);
      video.removeEventListener('error', finish);
      resolve();
    };
    const timeout = setTimeout(finish, VIDEO_FIRST_FRAME_TIMEOUT_MS);
    video.addEventListener('loadeddata', finish);
    video.addEventListener('error', finish);
  });
}

function createAudioLoader(url: string): Promise<HTMLAudioElement> {
  const audio = new Audio();
  audio.crossOrigin = 'Anonymous';
  audio.preload = 'auto';
  audio.src = url;
  return Promise.resolve(audio);
}

function createThreeDModelLoader(url: string): Promise<Uint8Array> {
  // Direct load: serve dropped bytes from memory, skipping the network fetch.
  const local = localModelBytes.get(url);
  if (local) {
    validateThreeDModelBytes(url, local);
    return Promise.resolve(local);
  }
  // A `local:` key with no bytes registered can't be fetched (it's not a real URL).
  if (url.startsWith(LOCAL_MODEL_KEY_PREFIX)) {
    return Promise.reject(new Error('No local bytes registered for key: ' + url));
  }
  return fetch(url)
    .then((res) => {
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      return res.bytes();
    })
    .then((bytes) => {
      // Throw here so `withRetry` can re-fetch truncated HTTP 2xx bodies.
      validateThreeDModelBytes(url, bytes);
      return bytes;
    })
    .catch((error) => {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error('Failed to load 3D model: ' + url + (message ? ': ' + message : ''));
    });
}

async function createFontLoader(
  resourceKey: string,
  familyKey: string,
  workflowCustomFontsUrlsMap: ReadonlyMap<string, string>,
) {
  try {
    const url = resolveFontFileUrl(resourceKey, familyKey, workflowCustomFontsUrlsMap);
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }
    return res.bytes();
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error('Failed to load font: ' + resourceKey + (message ? ': ' + message : ''));
  }
}

/**
 * Loads each entry's image from `fetchUrl` (its network locator, e.g. a Blobstore signed URL or Cloudinary URL),
 * and reports it back to `callback` under `key` (its stable cache identity).
 */
export async function loadImageResources(
  imagesToLoad: { key: string; fetchUrl: string }[],
  callback: (key: string, img: ImageBitmap | undefined) => void,
) {
  await Promise.all(
    imagesToLoad.map(async ({ key, fetchUrl }) => {
      let img: ImageBitmap | undefined = undefined;
      try {
        img = await loadFile(fetchUrl, {
          loader: createImageBitmapLoader,
          cacheKey: key,
        });
      } catch (e) {
        if (e instanceof Error) {
          logger.warn('Failed to load image resource: ' + key + ': ' + e.message, e);
        } else {
          logger.warn('Failed to load image resource: ' + key);
        }
      }
      callback(key, img);
    }),
  );
}

/**
 * Loads each entry's video from `fetchUrl` (its network locator, e.g. a Blobstore signed URL or Cloudinary URL),
 * and reports it back to `callback` under `key` (its stable cache identity).
 */
export async function loadVideoResources(
  videosToLoad: { key: string; fetchUrl: string }[],
  callback: (key: string, video: HTMLVideoElement | undefined) => void,
) {
  await Promise.all(
    videosToLoad.map(async ({ key, fetchUrl }) => {
      let video: HTMLVideoElement | undefined = undefined;
      try {
        video = await loadFile(fetchUrl, {
          loader: createVideoLoader,
          cacheKey: key,
        });
      } catch (e) {
        if (e instanceof Error) {
          logger.warn('Failed to load video resource: ' + key + ': ' + e.message, e);
        } else {
          logger.warn('Failed to load video resource: ' + key);
        }
      }
      callback(key, video);
    }),
  );
}

/**
 * Loads each entry's audio element from `fetchUrl` (its network locator) and reports it back to
 * `callback` under `key` (its stable cache identity). Like video, this constructs the element and
 * lets it stream on demand — it does not wait for the whole file to buffer.
 */
export async function loadAudioResources(
  audiosToLoad: { key: string; fetchUrl: string }[],
  callback: (key: string, audio: HTMLAudioElement | undefined) => void,
) {
  await Promise.all(
    audiosToLoad.map(async ({ key, fetchUrl }) => {
      let audio: HTMLAudioElement | undefined = undefined;
      try {
        audio = await loadFile(fetchUrl, {
          loader: createAudioLoader,
          cacheKey: key,
        });
      } catch (e) {
        if (e instanceof Error) {
          logger.warn('Failed to load audio resource: ' + key + ': ' + e.message, e);
        } else {
          logger.warn('Failed to load audio resource: ' + key);
        }
      }
      callback(key, audio);
    }),
  );
}

export async function loadThreeDModelResources(
  modelsToLoad: { key: string; fetchUrl: string }[],
  callback: (key: string, modelBytes: Uint8Array | undefined) => void,
) {
  await Promise.all(
    modelsToLoad.map(async ({ key, fetchUrl }) => {
      let modelBytes: Uint8Array | undefined = undefined;
      try {
        modelBytes = await loadFile(fetchUrl, {
          loader: createThreeDModelLoader,
          cacheKey: key,
        });
      } catch (e) {
        if (e instanceof Error) {
          logger.warn('Failed to load 3D model resource: ' + key + ': ' + e.message, e);
        } else {
          logger.warn('Failed to load 3D model resource: ' + key);
        }
      }
      callback(key, modelBytes);
    }),
  );
}

export async function loadFontResources(
  fontsToLoad: { key: string; familyKey: string }[],
  callback: (resourceKey: string, fontBytes: Uint8Array | undefined) => void,
  workflowCustomFontsUrlsMap: ReadonlyMap<string, string>,
) {
  await Promise.all(
    fontsToLoad.map(async ({ key: resourceKey, familyKey }) => {
      let fontBytes: Uint8Array | undefined = undefined;
      try {
        fontBytes = await loadFile(resourceKey, {
          loader: (key) => createFontLoader(key, familyKey, workflowCustomFontsUrlsMap),
        });
      } catch (e) {
        if (e instanceof Error) {
          logger.warn('Failed to load font resource: ' + resourceKey + ': ' + e.message, e);
        } else {
          logger.warn('Failed to load font resource: ' + resourceKey);
        }
      }
      callback(resourceKey, fontBytes);
    }),
  );
}
